"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { Button } from "@ui/components/button";
import {
	AlertCircle,
	Box,
	CheckCircle,
	Clock,
	Globe,
	ImageIcon,
	InfinityIcon,
	Loader2,
	X,
	Zap,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useDropzone } from "react-dropzone";
import { useBatchImageToPrompt } from "../hooks/use-batch-image-to-prompt";
import type { AIModel } from "../types";
import { BatchPromptResult } from "./BatchPromptResult";
import { LanguageSelector } from "./LanguageSelector";

export function BatchImagePromptConverter() {
	const t = useTranslations();
	const router = useRouter();
	const { user } = useSession();
	const {
		imageFiles,
		selectedModel,
		promptLanguage,
		results,
		processingStatus,
		usesRemaining,
		hasUnlimitedCredits,
		isPending,
		handleImagesSelected,
		removeImage,
		clearImages,
		processBatch,
		setSelectedModel,
		setPromptLanguage,
	} = useBatchImageToPrompt();

	// Handle button click for generation
	const handleGenerateClick = () => {
		// Check if user is logged in
		if (!user) {
			// Trigger login modal instead of redirect
			if (typeof window !== "undefined") {
				window.dispatchEvent(
					new CustomEvent("show-login", {
						detail: { redirectTo: "/batch-image-to-prompt" },
					}),
				);
			}
			return;
		}

		// Process the batch
		processBatch();
	};

	// Handle navigation to image generator
	const handleNavigateToImageGenerator = (prompt: string) => {
		// Store generated prompt in localStorage
		localStorage.setItem("transferredPrompt", prompt);
		// Navigate to image generator
		router.push("/ai-image-generator");
	};

	// Handle getting more credits
	const handleGetMoreCredits = () => {
		router.push("/app/subscription");
	};

	// File upload handler
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				// Combine with existing images
				handleImagesSelected([...imageFiles, ...acceptedFiles]);
			}
		},
		accept: {
			"image/png": [".png"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/webp": [".webp"],
		},
		maxSize: 10 * 1024 * 1024, // 10MB
		multiple: true,
	});

	return (
		<div className="space-y-6">
			<div>
				<div className="flex flex-col md:flex-row gap-6">
					{/* Upload area */}
					<div className="w-full md:w-1/2">
						<div className="border rounded-lg h-[422px]">
							<div className="p-4 h-full">
								<div
									{...getRootProps()}
									className={`
										h-full border-2 border-dashed rounded-md flex flex-col items-center justify-center cursor-pointer 
										${isDragActive ? "border-indigo-400 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"}
									`}
								>
									<input {...getInputProps()} />
									<ImageIcon className="h-12 w-12 text-indigo-400 mb-3" />
									<p className="text-gray-500 text-center mb-1">
										{t(
											"imageToPrompt.components.uploadTabs.uploadInstruction",
										)}
									</p>
									<p className="text-xs text-gray-400">
										{t(
											"imageToPrompt.components.uploadTabs.fileTypes",
											{ size: 4 },
										)}
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Preview area */}
					<div className="w-full md:w-1/2">
						<div className="border border-dashed rounded-lg h-[422px] flex flex-col">
							{imageFiles.length > 0 ? (
								<div className="p-4 flex-1 overflow-auto">
									<div className="flex justify-between items-center mb-3">
										<span className="text-sm font-medium text-gray-700">
											{t(
												"batchImageToPrompt.components.uploader.uploadedImages",
												{ count: imageFiles.length },
											)}
										</span>
										<Button
											variant="ghost"
											size="sm"
											onClick={clearImages}
											className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs"
										>
											{t(
												"batchImageToPrompt.components.uploader.clearAll",
											)}
										</Button>
									</div>

									<div className="max-h-[340px] overflow-y-auto pr-1">
										<div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
											{imageFiles.map((file, index) => (
												<div
													key={`${file.name}-${index}`}
													className="relative border rounded-md overflow-hidden group shadow-sm"
												>
													<div className="w-full aspect-square">
														<img
															src={URL.createObjectURL(
																file,
															)}
															alt={file.name}
															className="w-full h-full object-cover"
														/>
													</div>
													<div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
														<Button
															variant="ghost"
															size="icon"
															onClick={() =>
																removeImage(
																	index,
																)
															}
															className="bg-white/90 hover:bg-white text-red-500 h-5 w-5 rounded-full p-0"
														>
															<X className="h-3 w-3" />
														</Button>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							) : (
								<div className="flex-1 flex items-center justify-center text-gray-400 p-4">
									<div className="text-center">
										<ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
										<p>
											{t(
												"batchImageToPrompt.components.uploader.noImagesUploaded",
											)}
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="mt-6 border rounded-lg border-gray-200 p-6">
					<div className="space-y-6">
						<div>
							<h2 className="font-medium mb-3">
								{t(
									"batchImageToPrompt.components.converter.promptTarget",
								)}
							</h2>

							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
								{[
									{
										id: "flux" as AIModel,
										icon: Zap,
										label: t(
											"imageToPrompt.components.modelLabels.flux",
										),
									},
									{
										id: "general" as AIModel,
										icon: Globe,
										label: t(
											"imageToPrompt.components.modelLabels.general",
										),
									},
									{
										id: "stable-diffusion" as AIModel,
										icon: Box,
										label: t(
											"imageToPrompt.components.modelLabels.stableDiffusion",
										),
									},
									{
										id: "midjourney" as AIModel,
										icon: ImageIcon,
										label: t(
											"imageToPrompt.components.modelLabels.midjourney",
										),
									},
								].map((model) => (
									<button
										key={model.id}
										type="button"
										onClick={() =>
											setSelectedModel(model.id)
										}
										className={`relative flex items-center justify-center gap-1 p-3 rounded-lg transition-colors bg-gray-50 hover:bg-gray-100 ${
											selectedModel === model.id
												? "border-2 border-[#6366F1]"
												: "border border-gray-200"
										}`}
									>
										{selectedModel === model.id && (
											<div className="absolute top-2 right-2 text-[#6366F1]">
												<CheckCircle
													size={16}
													aria-label="Selected"
												/>
											</div>
										)}
										<model.icon
											className={
												selectedModel === model.id
													? "text-[#6366F1]"
													: "text-gray-500"
											}
											size={20}
											aria-label={`${model.label} icon`}
											aria-hidden="true"
										/>
										<span className="text-xs sm:text-sm font-medium truncate">
											{model.label}
										</span>
									</button>
								))}
							</div>
						</div>

						<div>
							<h2 className="font-medium mb-3">
								{t(
									"batchImageToPrompt.components.converter.language",
								)}
							</h2>
							<div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
								<div className="w-full sm:w-1/4">
									<LanguageSelector
										value={promptLanguage}
										onChange={setPromptLanguage}
									/>
								</div>

								<Button
									className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white py-2 px-8 rounded-lg h-12"
									disabled={
										isPending ||
										imageFiles.length === 0 ||
										(!!user &&
											usesRemaining !== "infinity" &&
											typeof usesRemaining === "number" &&
											usesRemaining < imageFiles.length)
									}
									onClick={handleGenerateClick}
								>
									{isPending ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{t(
												"batchImageToPrompt.components.converter.processing",
											)}
										</>
									) : (
										t(
											"batchImageToPrompt.components.converter.generatePrompts",
										)
									)}
								</Button>
							</div>
						</div>

						{user && (
							<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="font-medium text-sm">
										{t(
											"batchImageToPrompt.components.converter.creditsRemaining",
										)}
										:
									</span>
									<span className="flex items-center text-indigo-600 font-bold">
										{usesRemaining === "infinity" ? (
											<InfinityIcon className="h-4 w-4 text-indigo-500" />
										) : (
											<span>{usesRemaining} 💎</span>
										)}
									</span>
									<Button
										variant="link"
										size="sm"
										onClick={handleGetMoreCredits}
										className="text-indigo-600 px-1 h-7"
									>
										{t(
											"batchImageToPrompt.components.converter.getMoreCredits",
										)}
									</Button>
								</div>

								<Button
									variant="link"
									size="sm"
									onClick={() =>
										window.dispatchEvent(
											new Event("openPromptHistory"),
										)
									}
									className="text-indigo-600 flex items-center gap-1 h-7"
								>
									<Clock className="h-4 w-4" />
									{t(
										"imageToPrompt.components.promptConverter.viewHistory",
									)}
								</Button>
							</div>
						)}

						{user &&
							imageFiles.length > 0 &&
							usesRemaining !== "infinity" &&
							typeof usesRemaining === "number" &&
							usesRemaining < imageFiles.length && (
								<div className="flex items-center gap-2 p-2 bg-amber-50 text-amber-800 rounded-md text-sm">
									<AlertCircle className="h-4 w-4 flex-shrink-0" />
									<span>
										{t(
											"batchImageToPrompt.components.converter.notEnoughCredits",
											{
												needed: imageFiles.length,
												available: usesRemaining,
											},
										)}
									</span>
								</div>
							)}
					</div>
				</div>
			</div>

			{/* Results section */}
			{results.length > 0 && (
				<BatchPromptResult
					results={results}
					imageFiles={imageFiles}
					onUseInImageGenerator={handleNavigateToImageGenerator}
				/>
			)}
		</div>
	);
}
